import React, { useState } from 'react';
import { 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemText,
  ListItemAvatar,
  Avatar,
  Collapse, 
  Box, 
  Paper, 
  Chip,
  Dialog,
  DialogContent,
} from '@mui/material';
import { PersonOutline as PersonIcon } from '@mui/icons-material';
import { Event } from '../../types/Event';

interface ParticipantsListProps {
  event: Event;
  isAuthenticated: boolean;
  showParticipants: boolean;
  setShowParticipants: (show: boolean) => void;
}

interface Participant {
  _id: string;
  name: string;
  phone: string;
  spots: number;
  fee: number;
  checkedIn: boolean;
  paid: boolean;
  approved: boolean;
  avatarType?: 'initial' | 'image';
  avatarInitial?: string;
  avatarColor?: string;
  avatarUrl?: string;
}

const ParticipantsList: React.FC<ParticipantsListProps> = ({ 
  event, 
  isAuthenticated, 
  showParticipants, 
  setShowParticipants 
}) => {
  const [viewAvatarOpen, setViewAvatarOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);

  const handleAvatarClick = (participant: Participant) => {
    if (participant.avatarType === 'image') {
      setSelectedParticipant(participant);
      setViewAvatarOpen(true);
    }
  };

  return (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          參與者名單
        </Typography>
      </Box>
      <Button
        size="small"
        color="secondary"
        variant="outlined"
        onClick={() => setShowParticipants(!showParticipants)}
      >
        {showParticipants ? '隱藏名單' : '顯示名單'}
      </Button>
    </Box>
    
    <Collapse in={showParticipants}>
      <Paper variant="outlined" sx={{ px: 2, borderRadius: 2 }}>
        <List dense disablePadding>
          {event.participants && event.participants.length > 0 ? (
            event.participants.map((participant, index) => (
              <ListItem key={index} divider={index < event.participants!.length - 1}>
                <ListItemAvatar>
                  <Avatar
                    src={participant.avatarType === 'image' && participant.avatarUrl ? participant.avatarUrl : undefined}
                    sx={{
                      bgcolor: participant.avatarType === 'image' ? '#ffffff' : (participant.avatarColor || 'primary.main'),
                      border: '2px solid',
                      borderColor: participant.avatarType === 'image' ? 'divider' : 'transparent',
                      width: 40,
                      height: 40,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s'
                    }}
                    onClick={() => handleAvatarClick(participant)}
                  >
                    {participant.avatarType === 'initial' && participant.avatarInitial ? (
                      participant.avatarInitial
                    ) : participant.avatarType === 'image' ? null : (
                      participant.name ? participant.name.charAt(0).toUpperCase() : <PersonIcon />
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {participant.name} <Chip size="small" label={`${participant.spots} 位`} sx={{ ml: 1 }} />
                    </Typography>
                  }
                  secondary={isAuthenticated ? `電話: ${participant.phone}` : null}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="目前沒有參與者" />
            </ListItem>
          )}
        </List>
      </Paper>
    </Collapse>

    {/* Participant Avatar View Modal */}
    <Dialog 
      open={viewAvatarOpen} 
      onClose={() => setViewAvatarOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <Avatar 
            src={selectedParticipant?.avatarType === 'image' && selectedParticipant?.avatarUrl ? selectedParticipant.avatarUrl : undefined}
            sx={{ 
              bgcolor: selectedParticipant?.avatarType === 'image' ? '#ffffff' : (selectedParticipant?.avatarColor || 'primary.main'),
              border: '3px solid',
              borderColor: selectedParticipant?.avatarType === 'image' ? 'divider' : 'transparent',
              width: 200,
              height: 200,
              fontSize: '4rem',
            }}
          >
            {selectedParticipant?.avatarType === 'initial' && selectedParticipant?.avatarInitial 
              ? selectedParticipant.avatarInitial
              : selectedParticipant?.name ? selectedParticipant.name.charAt(0).toUpperCase() : <PersonIcon sx={{ fontSize: '4rem' }} />
            }
          </Avatar>
        </Box>
      </DialogContent>
    </Dialog>
  </Box>
  );
};

export default ParticipantsList;